import React, { useState, useEffect } from 'react';
import { SelectField } from "components/ui/select-field";
import {
  AIAgent,
  AgentCapability,
  AgentLearningData,
  CapabilityImprovement
} from '../../../types/aiAgent';

interface AIAgentTrainingProps {
  agent: AIAgent;
  onCapabilityUpdate?: (agentId: string, capabilities: AgentCapability[]) => void;
  onTrainingComplete?: (agentId: string, results: any) => void;
}

export const AIAgentTraining: React.FC<AIAgentTrainingProps> = ({
  agent,
  onCapabilityUpdate,
  onTrainingComplete
}) => {
  const [activeTab, setActiveTab] = useState<'capabilities' | 'learning' | 'training' | 'insights'>('capabilities');
  const [selectedCapability, setSelectedCapability] = useState<AgentCapability | null>(null);
  const [trainingInProgress, setTrainingInProgress] = useState(false);
  const [trainingProgress, setTrainingProgress] = useState(0);
  const [learningData, setLearningData] = useState<AgentLearningData>(agent.learningData);
  const [newTrainingData, setNewTrainingData] = useState<any[]>([]);

  useEffect(() => {
    setLearningData(agent.learningData);
  }, [agent]);

  const handleCapabilityTraining = async (capabilityId: string, trainingType: 'accuracy' | 'speed' | 'scope') => {
    try {
      setTrainingInProgress(true);
      setTrainingProgress(0);

      // Simulate training progress
      const progressInterval = setInterval(() => {
        setTrainingProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return prev + Math.random() * 15;
        });
      }, 500);

      // Simulate training completion after 5 seconds
      setTimeout(async () => {
        clearInterval(progressInterval);
        setTrainingProgress(100);

        // Update capability proficiency
        const updatedCapabilities = agent.capabilities.map(cap => 
          cap.id === capabilityId 
            ? { ...cap, proficiency: Math.min(cap.proficiency + 5 + Math.random() * 10, 100) }
            : cap
        );

        // Record capability improvement
        const improvement: CapabilityImprovement = {
          capabilityId,
          improvementType: trainingType,
          oldLevel: agent.capabilities.find(c => c.id === capabilityId)?.proficiency || 0,
          newLevel: updatedCapabilities.find(c => c.id === capabilityId)?.proficiency || 0,
          achievedAt: new Date(),
          method: `Targeted ${trainingType} training`,
          validationResults: { accuracy: 0.85 + Math.random() * 0.1 }
        };

        // Update learning data
        setLearningData(prev => ({
          ...prev,
          adaptations: {
            ...prev.adaptations,
            capabilityImprovements: [...prev.adaptations.capabilityImprovements, improvement]
          }
        }));

        onCapabilityUpdate?.(agent.id, updatedCapabilities);
        onTrainingComplete?.(agent.id, { improvement, capabilities: updatedCapabilities });

        setTrainingInProgress(false);
        setTrainingProgress(0);
      }, 5000);

    } catch (error) {
      console.error('Training failed:', error);
      setTrainingInProgress(false);
      setTrainingProgress(0);
    }
  };

  const handleBulkTraining = async (capabilityIds: string[]) => {
    try {
      setTrainingInProgress(true);
      setTrainingProgress(0);

      // Simulate bulk training
      for (let i = 0; i < capabilityIds.length; i++) {
        setTrainingProgress((i / capabilityIds.length) * 100);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      setTrainingProgress(100);
      setTrainingInProgress(false);
      
      // Update all selected capabilities
      const updatedCapabilities = agent.capabilities.map(cap => 
        capabilityIds.includes(cap.id)
          ? { ...cap, proficiency: Math.min(cap.proficiency + 3 + Math.random() * 7, 100) }
          : cap
      );

      onCapabilityUpdate?.(agent.id, updatedCapabilities);
      onTrainingComplete?.(agent.id, { type: 'bulk', capabilities: updatedCapabilities });

    } catch (error) {
      console.error('Bulk training failed:', error);
      setTrainingInProgress(false);
      setTrainingProgress(0);
    }
  };

  const addTrainingData = (data: any) => {
    setNewTrainingData(prev => [...prev, { ...data, id: Date.now(), timestamp: new Date() }]);
  };

  const removeTrainingData = (dataId: number) => {
    setNewTrainingData(prev => prev.filter(d => d.id !== dataId));
  };

  const _getCapabilityColor = (proficiency: number) => {
    if (proficiency >= 90) return 'text-green-600 dark:text-green-300 bg-green-100 dark:bg-green-900/30';
    if (proficiency >= 70) return 'text-blue-600 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30';
    if (proficiency >= 50) return 'text-yellow-600 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30';
    return 'text-red-600 dark:text-red-300 bg-red-100 dark:bg-red-900/30';
  };

  const _getCapabilityLevel = (proficiency: number) => {
    if (proficiency >= 90) return 'Expert';
    if (proficiency >= 70) return 'Advanced';
    if (proficiency >= 50) return 'Intermediate';
    return 'Beginner';
  };

  return (
    <div className="bg-surface rounded-lg shadow-sm border border-line">
      {/* Header */}
      <div className="border-b border-line px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-content">
              AI Agent Training - {agent.name}
            </h3>
            <p className="text-sm text-content-secondary mt-1">
              Enhance agent capabilities and monitor learning progress
            </p>
          </div>
          
          {trainingInProgress && (
            <div className="flex items-center space-x-3">
              <div className="w-32 bg-gray-200 dark:bg-dark-600 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${trainingProgress}%` }}
                ></div>
              </div>
              <span className="text-sm text-content-secondary">
                {trainingProgress.toFixed(0)}%
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-line">
        <nav className="flex space-x-8 px-6">
          {[
            { id: 'capabilities', label: 'Capabilities', icon: '🎯' },
            { id: 'learning', label: 'Learning Data', icon: '📚' },
            { id: 'training', label: 'Training Center', icon: '🏋️' },
            { id: 'insights', label: 'Insights', icon: '💡' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                  : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300 hover:border-line-strong'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'capabilities' && (
          <CapabilitiesTab
            agent={agent}
            onCapabilitySelect={setSelectedCapability}
            onTraining={handleCapabilityTraining}
            onBulkTraining={handleBulkTraining}
            trainingInProgress={trainingInProgress}
          />
        )}

        {activeTab === 'learning' && (
          <LearningDataTab
            learningData={learningData}
            agent={agent}
          />
        )}

        {activeTab === 'training' && (
          <TrainingCenterTab
            agent={agent}
            newTrainingData={newTrainingData}
            onAddTrainingData={addTrainingData}
            onRemoveTrainingData={removeTrainingData}
            trainingInProgress={trainingInProgress}
          />
        )}

        {activeTab === 'insights' && (
          <InsightsTab
            learningData={learningData}
            agent={agent}
          />
        )}
      </div>

      {/* Capability Detail Modal */}
      {selectedCapability && (
        <CapabilityDetailModal
          capability={selectedCapability}
          agent={agent}
          onClose={() => setSelectedCapability(null)}
          onTraining={handleCapabilityTraining}
        />
      )}
    </div>
  );
};

// Capabilities Tab
const CapabilitiesTab: React.FC<{
  agent: AIAgent;
  onCapabilitySelect: (capability: AgentCapability) => void;
  onTraining: (capabilityId: string, type: 'accuracy' | 'speed' | 'scope') => void;
  onBulkTraining: (capabilityIds: string[]) => void;
  trainingInProgress: boolean;
}> = ({ agent, onCapabilitySelect, onTraining, onBulkTraining, trainingInProgress }) => {
  const [selectedCapabilities, setSelectedCapabilities] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<'proficiency' | 'usage' | 'name'>('proficiency');

  const getCapabilityColor = (proficiency: number) => {
    if (proficiency >= 90) return 'text-green-600 dark:text-green-300 bg-green-100 dark:bg-green-900/30';
    if (proficiency >= 70) return 'text-blue-600 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30';
    if (proficiency >= 50) return 'text-yellow-600 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30';
    return 'text-red-600 dark:text-red-300 bg-red-100 dark:bg-red-900/30';
  };

  const getCapabilityLevel = (proficiency: number) => {
    if (proficiency >= 90) return 'Expert';
    if (proficiency >= 70) return 'Advanced';
    if (proficiency >= 50) return 'Intermediate';
    return 'Beginner';
  };

  const sortedCapabilities = [...agent.capabilities].sort((a, b) => {
    switch (sortBy) {
      case 'proficiency':
        return b.proficiency - a.proficiency;
      case 'usage':
        return b.usageCount - a.usageCount;
      case 'name':
        return a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });

  const toggleCapabilitySelection = (capabilityId: string) => {
    const newSelection = new Set(selectedCapabilities);
    if (newSelection.has(capabilityId)) {
      newSelection.delete(capabilityId);
    } else {
      newSelection.add(capabilityId);
    }
    setSelectedCapabilities(newSelection);
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <SelectField
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="border border-line-strong rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="proficiency">Sort by Proficiency</option>
            <option value="usage">Sort by Usage</option>
            <option value="name">Sort by Name</option>
          </SelectField>
          
          {selectedCapabilities.size > 0 && (
            <button
              onClick={() => onBulkTraining(Array.from(selectedCapabilities))}
              disabled={trainingInProgress}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Train Selected ({selectedCapabilities.size})
            </button>
          )}
        </div>

        <div className="text-sm text-content-secondary">
          {agent.capabilities.length} capabilities total
        </div>
      </div>

      {/* Capability Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedCapabilities.map(capability => (
          <div
            key={capability.id}
            className="border border-line rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={selectedCapabilities.has(capability.id)}
                  onChange={() => toggleCapabilitySelection(capability.id)}
                />
                <h4 className="font-medium text-content">{capability.name}</h4>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCapabilityColor(capability.proficiency)}`}>
                {getCapabilityLevel(capability.proficiency)}
              </span>
            </div>

            <p className="text-sm text-content-secondary mb-3">{capability.description}</p>

            {/* Proficiency Bar */}
            <div className="mb-3">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-content-secondary">Proficiency</span>
                <span className="font-medium">{capability.proficiency.toFixed(0)}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-dark-600 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full"
                  style={{ width: `${capability.proficiency}%` }}
                ></div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-2 text-xs text-content-secondary mb-3">
              <div>Used: {capability.usageCount} times</div>
              <div>Success: {(capability.successRate * 100).toFixed(0)}%</div>
              <div>Type: {capability.type}</div>
              <div>
                Last used: {capability.lastUsed ? capability.lastUsed.toLocaleDateString() : 'Never'}
              </div>
            </div>

            {/* Actions */}
            <div className="flex space-x-2">
              <button
                onClick={() => onCapabilitySelect(capability)}
                className="flex-1 px-3 py-1 text-sm border border-line-strong rounded hover:bg-gray-50 dark:hover:bg-dark-700"
              >
                Details
              </button>
              <button
                onClick={() => onTraining(capability.id, 'accuracy')}
                disabled={trainingInProgress}
                className="flex-1 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                Train
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Learning Data Tab
const LearningDataTab: React.FC<{
  learningData: AgentLearningData;
  agent: AIAgent;
}> = ({ learningData, agent: _agent }) => {
  const [selectedSection, setSelectedSection] = useState<'patterns' | 'practices' | 'failures' | 'adaptations'>('patterns');

  return (
    <div className="space-y-6">
      {/* Learning Overview */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-300">{learningData.knowledgeBase.patterns.length}</div>
          <div className="text-sm text-blue-600 dark:text-blue-300">Learned Patterns</div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600 dark:text-green-300">{learningData.knowledgeBase.bestPractices.length}</div>
          <div className="text-sm text-green-600 dark:text-green-300">Best Practices</div>
        </div>
        <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-4">
          <div className="text-2xl font-bold text-red-600 dark:text-red-300">{learningData.knowledgeBase.failures.length}</div>
          <div className="text-sm text-red-600 dark:text-red-300">Failure Cases</div>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4">
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-300">{learningData.adaptations.capabilityImprovements.length}</div>
          <div className="text-sm text-purple-600 dark:text-purple-300">Improvements</div>
        </div>
      </div>

      {/* Section Navigation */}
      <div className="flex space-x-4 border-b border-line">
        {[
          { id: 'patterns', label: 'Learned Patterns' },
          { id: 'practices', label: 'Best Practices' },
          { id: 'failures', label: 'Failure Analysis' },
          { id: 'adaptations', label: 'Adaptations' }
        ].map(section => (
          <button
            key={section.id}
            onClick={() => setSelectedSection(section.id as any)}
            className={`py-2 px-4 border-b-2 font-medium text-sm ${
              selectedSection === section.id
                ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {section.label}
          </button>
        ))}
      </div>

      {/* Section Content */}
      <div>
        {selectedSection === 'patterns' && (
          <div className="space-y-4">
            {learningData.knowledgeBase.patterns.map(pattern => (
              <div key={pattern.id} className="border border-line rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-content">{pattern.pattern}</h4>
                  <span className="text-sm text-content-secondary">
                    {(pattern.successRate * 100).toFixed(0)}% success rate
                  </span>
                </div>
                <p className="text-sm text-content-secondary mb-2">
                  Context: {pattern.context.join(', ')}
                </p>
                <div className="text-xs text-content-muted">
                  Observed {pattern.frequency} times • 
                  First seen: {pattern.firstObserved.toLocaleDateString()} • 
                  Last seen: {pattern.lastObserved.toLocaleDateString()}
                </div>
              </div>
            ))}
            {learningData.knowledgeBase.patterns.length === 0 && (
              <div className="text-center py-8 text-content-muted">
                No learned patterns yet
              </div>
            )}
          </div>
        )}

        {selectedSection === 'practices' && (
          <div className="space-y-4">
            {learningData.knowledgeBase.bestPractices.map(practice => (
              <div key={practice.id} className="border border-line rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-content">{practice.title}</h4>
                  <span className="text-sm text-content-secondary">
                    {(practice.effectiveness * 100).toFixed(0)}% effective
                  </span>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{practice.description}</p>
                <div className="text-xs text-content-muted">
                  Category: {practice.category} • 
                  Validated {practice.validatedCount} times • 
                  Last validated: {practice.lastValidated.toLocaleDateString()}
                </div>
              </div>
            ))}
            {learningData.knowledgeBase.bestPractices.length === 0 && (
              <div className="text-center py-8 text-content-muted">
                No best practices identified yet
              </div>
            )}
          </div>
        )}

        {selectedSection === 'failures' && (
          <div className="space-y-4">
            {learningData.knowledgeBase.failures.map(failure => (
              <div key={failure.id} className="border border-red-200 dark:border-red-800/50 rounded-lg p-4 bg-red-50 dark:bg-red-900/30">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-red-900 dark:text-red-300">{failure.description}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    failure.resolved ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                  }`}>
                    {failure.resolved ? 'Resolved' : 'Unresolved'}
                  </span>
                </div>
                <p className="text-sm text-red-700 dark:text-red-300 mb-2">
                  <strong>Root Cause:</strong> {failure.rootCause}
                </p>
                <p className="text-sm text-red-700 dark:text-red-300 mb-2">
                  <strong>Prevention:</strong> {failure.preventionStrategy}
                </p>
                <div className="text-xs text-red-600 dark:text-red-300">
                  Occurred: {failure.occurredAt.toLocaleDateString()} • 
                  Learnings applied: {failure.learningsApplied.join(', ')}
                </div>
              </div>
            ))}
            {learningData.knowledgeBase.failures.length === 0 && (
              <div className="text-center py-8 text-content-muted">
                No failure cases recorded
              </div>
            )}
          </div>
        )}

        {selectedSection === 'adaptations' && (
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-content mb-3">Capability Improvements</h4>
              {learningData.adaptations.capabilityImprovements.map((improvement, index) => (
                <div key={index} className="border border-line rounded-lg p-4 mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-content">
                      {improvement.capabilityId} - {improvement.improvementType}
                    </span>
                    <span className="text-sm text-green-600 dark:text-green-300">
                      +{(improvement.newLevel - improvement.oldLevel).toFixed(1)} points
                    </span>
                  </div>
                  <div className="text-sm text-content-secondary">
                    {improvement.oldLevel.toFixed(1)}% → {improvement.newLevel.toFixed(1)}% • 
                    Method: {improvement.method} • 
                    {improvement.achievedAt.toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>

            <div>
              <h4 className="font-medium text-content mb-3">Behavior Changes</h4>
              {learningData.adaptations.behaviorChanges.map((change, index) => (
                <div key={index} className="border border-line rounded-lg p-4 mb-3">
                  <div className="font-medium text-content mb-2">
                    Trigger: {change.trigger}
                  </div>
                  <div className="text-sm text-content-secondary mb-2">
                    <strong>Old:</strong> {change.oldBehavior}
                  </div>
                  <div className="text-sm text-content-secondary mb-2">
                    <strong>New:</strong> {change.newBehavior}
                  </div>
                  <div className="text-xs text-content-muted">
                    Reason: {change.reason} • 
                    Effectiveness: {(change.effectiveness * 100).toFixed(0)}% • 
                    {change.implementedAt.toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Training Center Tab
const TrainingCenterTab: React.FC<{
  agent: AIAgent;
  newTrainingData: any[];
  onAddTrainingData: (data: any) => void;
  onRemoveTrainingData: (dataId: number) => void;
  trainingInProgress: boolean;
}> = ({ agent, newTrainingData, onAddTrainingData, onRemoveTrainingData, trainingInProgress }) => {
  const [trainingType, setTrainingType] = useState<'scenario' | 'feedback' | 'pattern'>('scenario');
  const [trainingContent, setTrainingContent] = useState('');
  const [expectedOutcome, setExpectedOutcome] = useState('');

  const handleAddTraining = () => {
    if (!trainingContent.trim()) return;

    const trainingData = {
      type: trainingType,
      content: trainingContent,
      expectedOutcome: expectedOutcome || undefined,
      difficulty: 'medium',
      category: agent.specialization
    };

    onAddTrainingData(trainingData);
    setTrainingContent('');
    setExpectedOutcome('');
  };

  return (
    <div className="space-y-6">
      {/* Training Data Input */}
      <div className="border border-line rounded-lg p-6">
        <h4 className="text-lg font-medium text-content mb-4">Add Training Data</h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Training Type
            </label>
            <SelectField
              value={trainingType}
              onChange={(e) => setTrainingType(e.target.value as any)}
              className="w-full border border-line-strong rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="scenario">Scenario-based Training</option>
              <option value="feedback">Feedback Integration</option>
              <option value="pattern">Pattern Recognition</option>
            </SelectField>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Training Content
            </label>
            <textarea
              value={trainingContent}
              onChange={(e) => setTrainingContent(e.target.value)}
              placeholder="Describe the training scenario, feedback, or pattern..."
              rows={4}
              className="w-full border border-line-strong rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Expected Outcome (Optional)
            </label>
            <input
              type="text"
              value={expectedOutcome}
              onChange={(e) => setExpectedOutcome(e.target.value)}
              placeholder="What should the agent learn from this?"
              className="w-full border border-line-strong rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            onClick={handleAddTraining}
            disabled={!trainingContent.trim() || trainingInProgress}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            Add Training Data
          </button>
        </div>
      </div>

      {/* Training Queue */}
      <div>
        <h4 className="text-lg font-medium text-content mb-4">
          Training Queue ({newTrainingData.length})
        </h4>
        
        {newTrainingData.length > 0 ? (
          <div className="space-y-3">
            {newTrainingData.map(data => (
              <div key={data.id} className="border border-line rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs rounded-full">
                        {data.type}
                      </span>
                      <span className="text-sm text-content-secondary">
                        {data.timestamp.toLocaleString()}
                      </span>
                    </div>
                    <p className="text-content mb-2">{data.content}</p>
                    {data.expectedOutcome && (
                      <p className="text-sm text-content-secondary">
                        <strong>Expected:</strong> {data.expectedOutcome}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => onRemoveTrainingData(data.id)}
                    className="text-red-600 dark:text-red-300 hover:text-red-800 dark:hover:text-red-300"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
            
            <button
              onClick={() => {/* Implement batch training */}}
              disabled={trainingInProgress}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              Start Training Session
            </button>
          </div>
        ) : (
          <div className="text-center py-8 text-content-muted">
            No training data in queue
          </div>
        )}
      </div>

      {/* Training History */}
      <div>
        <h4 className="text-lg font-medium text-content mb-4">Training History</h4>
        <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
          <div className="text-sm text-content-secondary">
            <div>Last Training: {agent.learningData.trainingData.lastTrainingDate.toLocaleDateString()}</div>
            <div>Training Data Size: {agent.learningData.trainingData.trainingDataSize} samples</div>
            <div>Model Version: {agent.learningData.trainingData.modelVersion}</div>
            <div>Accuracy Metrics: {Object.keys(agent.learningData.trainingData.accuracyMetrics).length} metrics tracked</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Insights Tab
const InsightsTab: React.FC<{
  learningData: AgentLearningData;
  agent: AIAgent;
}> = ({ learningData, agent: _agent }) => {
  const insights = learningData.knowledgeBase.insights;

  const getInsightColor = (impact: string) => {
    const colors = {
      high: 'text-red-600 dark:text-red-300 bg-red-100 dark:bg-red-900/30',
      medium: 'text-yellow-600 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30',
      low: 'text-green-600 dark:text-green-300 bg-green-100 dark:bg-green-900/30'
    };
    return colors[impact as keyof typeof colors] || 'text-content-secondary bg-surface-sunken';
  };

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-content mb-4">AI-Generated Insights</h4>
        <p className="text-sm text-content-secondary mb-6">
          Insights discovered through agent learning and pattern analysis
        </p>
      </div>

      <div className="space-y-4">
        {insights.map(insight => (
          <div key={insight.id} className="border border-line rounded-lg p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <h4 className="text-lg font-medium text-content">{insight.title}</h4>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getInsightColor(insight.impact)}`}>
                  {insight.impact.toUpperCase()} IMPACT
                </span>
                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs rounded-full">
                  {insight.type.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <div className="text-sm text-content-secondary">
                {(insight.confidence * 100).toFixed(0)}% confidence
              </div>
            </div>

            <p className="text-gray-700 dark:text-gray-300 mb-4">{insight.description}</p>

            {insight.actionable && insight.recommendations.length > 0 && (
              <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4 mb-4">
                <h5 className="font-medium text-blue-900 dark:text-blue-300 mb-2">Recommendations</h5>
                <ul className="list-disc list-inside text-sm text-blue-800 dark:text-blue-300 space-y-1">
                  {insight.recommendations.map((rec, index) => (
                    <li key={index}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center justify-between text-sm text-content-muted">
              <span>Discovered: {insight.discoveredAt.toLocaleDateString()}</span>
              <span>{insight.supportingData.length} data points</span>
            </div>
          </div>
        ))}

        {insights.length === 0 && (
          <div className="text-center py-12">
            <div className="text-content-subtle text-6xl mb-4">💡</div>
            <h3 className="text-lg font-medium text-content mb-2">No insights yet</h3>
            <p className="text-content-secondary">
              Insights will be generated as the agent learns and identifies patterns
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Capability Detail Modal
const CapabilityDetailModal: React.FC<{
  capability: AgentCapability;
  agent: AIAgent;
  onClose: () => void;
  onTraining: (capabilityId: string, type: 'accuracy' | 'speed' | 'scope') => void;
}> = ({ capability, agent: _agent, onClose, onTraining }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-surface rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-line flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-content">{capability.name}</h3>
            <p className="text-sm text-content-secondary mt-1">{capability.description}</p>
          </div>
          <button
            onClick={onClose}
            className="text-content-subtle hover:text-content-secondary"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          <div className="space-y-6">
            {/* Proficiency */}
            <div>
              <h4 className="font-medium text-content mb-3">Current Proficiency</h4>
              <div className="flex items-center space-x-4">
                <div className="flex-1 bg-gray-200 dark:bg-dark-600 rounded-full h-3">
                  <div
                    className="bg-blue-600 h-3 rounded-full"
                    style={{ width: `${capability.proficiency}%` }}
                  ></div>
                </div>
                <span className="font-medium text-content">
                  {capability.proficiency.toFixed(1)}%
                </span>
              </div>
            </div>

            {/* Statistics */}
            <div>
              <h4 className="font-medium text-content mb-3">Usage Statistics</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
                  <div className="text-2xl font-bold text-content">{capability.usageCount}</div>
                  <div className="text-sm text-content-secondary">Times Used</div>
                </div>
                <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
                  <div className="text-2xl font-bold text-content">
                    {(capability.successRate * 100).toFixed(1)}%
                  </div>
                  <div className="text-sm text-content-secondary">Success Rate</div>
                </div>
              </div>
            </div>

            {/* Prerequisites */}
            {capability.prerequisites.length > 0 && (
              <div>
                <h4 className="font-medium text-content mb-3">Prerequisites</h4>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                  {capability.prerequisites.map((prereq, index) => (
                    <li key={index}>{prereq}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Training Options */}
            <div>
              <h4 className="font-medium text-content mb-3">Training Options</h4>
              <div className="space-y-3">
                <button
                  onClick={() => onTraining(capability.id, 'accuracy')}
                  className="w-full p-4 border border-line rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 text-left"
                >
                  <div className="font-medium text-content">Accuracy Training</div>
                  <div className="text-sm text-content-secondary">Improve decision accuracy and reduce errors</div>
                </button>
                <button
                  onClick={() => onTraining(capability.id, 'speed')}
                  className="w-full p-4 border border-line rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 text-left"
                >
                  <div className="font-medium text-content">Speed Training</div>
                  <div className="text-sm text-content-secondary">Reduce response time and increase efficiency</div>
                </button>
                <button
                  onClick={() => onTraining(capability.id, 'scope')}
                  className="w-full p-4 border border-line rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 text-left"
                >
                  <div className="font-medium text-content">Scope Training</div>
                  <div className="text-sm text-content-secondary">Expand capability to handle more scenarios</div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-line flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-line-strong rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};